package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initSilenceIsNotACleanRunPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/SilenceIsNotACleanRun.md", FrontMatterElement.Builder { addScalar("title", "Silence is not a clean run"); addScalar("searchTitle", "Mutation testing and fuzzing on the JVM — when the instrument reports nothing"); addScalar("date", "2026-08-27"); addScalar("description", "Durability that 779 tests could not see, a fuzzer that needed two files to get through the door, and a harness that reported targets it never ran."); addScalar("library", "bochka"); }))
}

@Page("/blog/silence-is-not-a-clean-run")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun SilenceIsNotACleanRunPage() {
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/bochka") {
            org.jetbrains.compose.web.dom.Text("bochka")
        }
        org.jetbrains.compose.web.dom.Text(" is an S3-compatible object store on the JVM: one process, one node, one disk. It passes 518 of the 744 cases in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ceph/s3-tests")
        }
        org.jetbrains.compose.web.dom.Text(" with a classified reason on every remaining failure, and the gate is 933 tests.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Four milestones went into asking whether those tests check anything — mutation testing, then fuzzing. The instruments found real gaps. Each of them also reported, at least once, that all was well when it had done nothing at all; those two outputs are byte-identical.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("you-cannot-test-fsync-from-inside-a-jvm") }) {
        org.jetbrains.compose.web.dom.Text("You cannot test ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("fsync")
        }
        org.jetbrains.compose.web.dom.Text(" from inside a JVM")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The write order is the rule the store is built around: file, flush, then the index record. The wrong order leaves a dangling reference after a crash — a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("500")
        }
        org.jetbrains.compose.web.dom.Text(" on a key the server itself named. There are crash tests for it: a child JVM writes, the parent ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("SIGKILL")
        }
        org.jetbrains.compose.web.dom.Text("s it mid-write, then reopens the store and requires everything the log admitted to to read back and match its checksum.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Removing ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("both")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("fsync")
        }
        org.jetbrains.compose.web.dom.Text(" calls leaves all ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("779 tests green")
        }
        org.jetbrains.compose.web.dom.Text(" — the whole gate at the time, crash tests included.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("SIGKILL")
        }
        org.jetbrains.compose.web.dom.Text(" kills a process; the page cache belongs to the machine. The parent reads back everything the child wrote whether or not a byte was ever flushed, so the crash test checks the ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("order")
        }
        org.jetbrains.compose.web.dom.Text(" of writes — a real property, and not this one. Durability is unobservable from inside the JVM under test, which means no test there can be written to see it, and the absence of one looks exactly like a passing one. Mutation testing could not name it either: pitest swaps classes through a loader in its own JVM while the writer is a different process, so a mutation there survives always — a property of the harness, not of the tests.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A bytecode gate holds it now: seven functions that must contain a call to ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("force")
        }
        org.jetbrains.compose.web.dom.Text(", each with its reason beside it. The gate also states what it does ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("not")
        }
        org.jetbrains.compose.web.dom.Text(" promise — that the call is present, yes; that it is in the right place, no. A guard overstating its reach is the next thing to be trusted wrongly.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("two-seed-files-were-worth-twenty-seven-million-inputs") }) {
        org.jetbrains.compose.web.dom.Text("Two seed files were worth twenty-seven million inputs")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The HTTP head parser is the first code an unauthenticated stranger reaches — the specification requires a refusal to be possible from the headers alone, so parsing happens before any signature is checked. Pointing a coverage-guided fuzzer at it, with an empty corpus:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""Done 27820013 runs in 61 second(s)
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Nothing. Then two seed files — an ordinary request, and one carrying both ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Content-Length")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Transfer-Encoding")
        }
        org.jetbrains.compose.web.dom.Text(" — and it found something on input ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("24")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Same target, same machine, same minute of wall clock. Random bytes essentially never spell ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("<method> <target> <version>")
        }
        org.jetbrains.compose.web.dom.Text(", so everything behind the request line is unreachable: a corpus-less run measures the corridor in front of the first door, and reports it clean.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("What it reached, once let in, was ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("S3Router.route")
        }
        org.jetbrains.compose.web.dom.Text(" — a function with no ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("throw")
        }
        org.jetbrains.compose.web.dom.Text(" anywhere in its source, which is what made \"it never throws\" worth asserting. Its query parser reaches the percent-decoder, where a truncated ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("%")
        }
        org.jetbrains.compose.web.dom.Text(" came back as a bare ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("IllegalArgumentException")
        }
        org.jetbrains.compose.web.dom.Text(" and the generic failure handler answered ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("500 InternalError")
        }
        org.jetbrains.compose.web.dom.Text(". The connection does not drop; that half was fixed a milestone earlier, when a malformed header closed the socket with no bytes in it. The status rode along behind the fix untouched — and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("500")
        }
        org.jetbrains.compose.web.dom.Text(" tells a client to retry a request that can never succeed, which ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("aws-cli")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("boto3")
        }
        org.jetbrains.compose.web.dom.Text(" both do, five times.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("my-own-harness-reported-targets-it-never-ran-twice") }) {
        org.jetbrains.compose.web.dom.Text("My own harness reported targets it never ran, twice")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The run script prints how many targets it fuzzed. It said two; it had fuzzed one. libFuzzer takes the process over, so one JVM fuzzes one target and the rest never start — while the count came from counting source files. That is exactly the guard the script exists to be, and it failed by counting the wrong noun. Fixed to one process per class, three commits later it claimed four targets having run three: the process is owned per ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("method")
        }
        org.jetbrains.compose.web.dom.Text(", and one class had two.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The count comes from the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Done N runs")
        }
        org.jetbrains.compose.web.dom.Text(" line now, which only libFuzzer can print, and a run where a target did not fuzz is red rather than quiet. Confirmed with a decoy that looks like a target and fuzzes nothing:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-text") }) { org.jetbrains.compose.web.dom.Text("""FAIL    DecoyFuzzTest did not fuzz at all — no libFuzzer run in its output
only 2 of 3 targets fuzzed, which is a failure rather than a clean run
""") } }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-rest-of-it") }) {
        org.jetbrains.compose.web.dom.Text("The rest of it")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("A mutation percentage measures its denominator. Of 4618 mutations, ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("499 were written by the compiler")
            }
            org.jetbrains.compose.web.dom.Text(" — null-check intrinsics, coroutine resumption, ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("data class")
            }
            org.jetbrains.compose.web.dom.Text(" equality. That ratio moves when you add a ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("data class")
            }
            org.jetbrains.compose.web.dom.Text(", so the report is read as four named buckets and never as a score.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Totals from a mutation run repeat; a verdict on one mutant does not. Two runs over identical code disagreed about the ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("fsync")
            }
            org.jetbrains.compose.web.dom.Text(" mutants, and naming the killer by hand showed the report was wrong. A rule explaining a survivor is written after a manual substitution, not after a line in a report.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Twelve survivors in the routing cascade were reported at lines 747–794 of a 742-line file: Kotlin encodes inlined code through SMAP with numbers past the end of the source. Both obvious guesses were checked by hand and both are caught by the suite, so those twelve stay filed as unexplained rather than explained wrongly.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("libFuzzer will not generate an input over 4096 bytes and the shipped limits are 8 KiB a line, so a target built on the real constants cannot reach the branches enforcing them — and reports full coverage of everything except the bounds it exists for.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Three times the assertion in a fuzz target was wrong rather than the code: ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("505")
            }
            org.jetbrains.compose.web.dom.Text(" for an unknown HTTP version is correct, a ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("content-length-range")
            }
            org.jetbrains.compose.web.dom.Text(" need not be ordered, and ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("route")
            }
            org.jetbrains.compose.web.dom.Text(" is not total. The third found the defect ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("because")
            }
            org.jetbrains.compose.web.dom.Text(" it was too strong, then was weakened. A target's assertion is the weakest true one, and a stronger one is kept exactly until it fires.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The seed corpus is ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("-text")
            }
            org.jetbrains.compose.web.dom.Text(" in ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text(".gitattributes")
            }
            org.jetbrains.compose.web.dom.Text(", because in an HTTP seed the CRLF is the framing. A ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("git stash")
            }
            org.jetbrains.compose.web.dom.Text(" round trip taken before that rule existed stripped the CR out of all three seeds, and each went on passing — as a \"line without CRLF\" refusal rather than the request it was written as. Nothing went red, because a corpus file has no expected result.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("ask-what-the-tool-would-say-if-it-had-done-nothing") }) {
        org.jetbrains.compose.web.dom.Text("Ask what the tool would say if it had done nothing")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Every failure above has the same shape. A gate with the durability removed, a fuzzer that never got past the request line, a harness that ran one target of two — each produced the output of a clean run, because ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("a tool that finds nothing and a tool that does nothing are indistinguishable from outside")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So the question to ask of a green instrument is not whether it passed, but: ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("what would this report if it had done no work at all, and how would I tell that apart from what it just told me?")
        }
        org.jetbrains.compose.web.dom.Text(" For the harness that meant counting the line only libFuzzer can print; for durability, admitting no test in that JVM can see it and moving the check to the bytecode; for the corpus, noticing that twenty-seven million of anything ought to have found ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("something")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("An instrument is a stand, and a stand is checked before its output is allowed to become a claim.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/bochka") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/bochka")
        }
    }
}
